<template>
  <section class="page" data-testid="page-qqlogin">
    <header class="page-header">
      <div><h1>QQ 登录</h1><p>NapCat 登录态、二维码、快速登录和 OneBot 连接状态。</p></div>
      <div class="actions">
        <button class="button" @click="toggleAutoRefresh">{{ autoRefresh ? "暂停自动刷新" : "开启自动刷新" }}</button>
        <button class="button" :disabled="!Object.keys(status).length" @click="copyStatus">复制状态</button>
        <button class="button primary" :disabled="loading" @click="() => loadAll()">{{ loading ? "刷新中..." : "刷新" }}</button>
      </div>
    </header>
    <div class="metrics-grid">
      <MetricCard label="QQ" :value="status.uin || '--'" />
      <MetricCard label="昵称" :value="status.nick || '--'" />
      <MetricCard label="登录态" :value="statusText" />
      <MetricCard label="OneBot" :value="status.onebot_connected ? '已连接' : '未连接'" />
      <MetricCard label="回复就绪" :value="onebotReplyReadyLabel" />
      <MetricCard label="账号缓存" :value="quickList.length" />
      <MetricCard label="二维码" :value="qrcodeState" />
      <MetricCard label="自动刷新" :value="autoRefresh ? '开启' : '暂停'" />
      <MetricCard label="最近刷新" :value="lastLoadedAt ? lastLoadedAt.toLocaleTimeString('zh-CN') : '-'" />
    </div>
    <p v-if="error" class="error-text">{{ error }}</p>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>
    <div class="split">
      <div class="panel">
        <h2>二维码</h2>
        <div class="login-verdict" :class="connectionLevel">
          <strong>{{ connectionVerdict }}</strong>
          <span>{{ connectionHint }}</span>
        </div>
        <div class="empty" v-if="qrLoading">正在获取二维码...</div>
        <div v-else-if="qrcode" style="text-align:center">
          <img :src="qrcodeDataUrl" class="qrcode" data-testid="qqlogin-qrcode" />
          <p v-if="visibleQrcodeUrl"><a :href="visibleQrcodeUrl" target="_blank" rel="noopener">打开登录链接</a></p>
        </div>
        <div v-else class="empty">{{ status.qrcode_url ? "NapCat 已返回登录链接，可直接打开。" : "点击按钮获取二维码" }}</div>
        <div class="actions">
          <button class="button primary" :disabled="qrLoading" data-testid="qqlogin-qrcode-submit" @click="getQrcode">{{ qrLoading ? "获取中..." : (qrcode ? "重新获取" : "获取二维码") }}</button>
          <button class="button" :disabled="qrLoading || !qrcode" @click="refreshQrcode">刷新二维码</button>
          <a v-if="visibleQrcodeUrl" class="button" :href="visibleQrcodeUrl" target="_blank" rel="noopener">打开链接</a>
        </div>
      </div>
      <div>
        <div class="panel">
          <h2>快速登录</h2>
          <div class="chips">
            <button
              v-for="qq in quickList"
              :key="qqKey(qq)"
              class="account-chip"
              :disabled="!!quickLoadingUin"
              @click="quickLogin(qqUin(qq))"
            >
              <strong>{{ qqLabel(qq) }}</strong>
              <span>{{ quickLoadingUin === qqUin(qq) ? "登录中..." : quickStatus(qq) }}</span>
            </button>
          </div>
          <div v-if="!quickList.length" class="empty">暂无保存账号</div>
        </div>
        <div class="panel">
          <h2>NapCat</h2>
          <div class="diagnostic-grid">
            <div v-for="item in diagnostics" :key="item.label" :class="item.level">
              <span>{{ item.label }}</span>
              <strong>{{ item.value }}</strong>
            </div>
          </div>
          <div class="actions">
            <button class="button" :disabled="onebotReconnecting" @click="reconnectOneBot">{{ onebotReconnecting ? "重连中..." : "重连 OneBot" }}</button>
            <button class="button danger" :disabled="restarting" @click="restart">{{ restarting ? "重启中..." : "重启 NapCat" }}</button>
            <button class="button" @click="copyStatus">复制诊断</button>
          </div>
          <details class="raw-status">
            <summary>原始状态</summary>
            <pre>{{ formattedStatus }}</pre>
          </details>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { api, postJson } from "../api/client";
import type { NapCatQuickLoginAccount, NapCatStatusPayload, OneBotIngressVerdict, OneBotRuntimePayload } from "../api/types";
import { useClipboardResult } from "../composables/useClipboardResult";
import { useOneBotReconnect } from "../composables/useOneBotReconnect";
import { napcatLoginRecoveryActionLabel } from "../diagnostics/napcat";
import {
  onebotDiagnosisLabel,
  onebotIngressLabel,
  onebotReplyReadyLabel as formatOnebotReplyReadyLabel
} from "../diagnostics/onebot";
import { useToastStore } from "../stores/toast";
import MetricCard from "../ui/MetricCard.vue";

const toast = useToastStore();
const status = ref<Partial<NapCatStatusPayload>>({});
const qrcode = ref("");
const qrcodeUrl = ref("");
const quickList = ref<NapCatQuickLoginAccount[]>([]);
const loading = ref(false);
const qrLoading = ref(false);
const quickLoadingUin = ref("");
const restarting = ref(false);
const error = ref("");
const actionResult = ref("");
const { copyJson } = useClipboardResult(actionResult, "浏览器不允许复制，请手动选择原始状态");
const { reconnecting: onebotReconnecting, reconnectOneBot } = useOneBotReconnect({
  actionResult,
  error,
  onSuccess(payload) {
    if (payload.onebot) {
      status.value = {
        ...status.value,
        onebot: payload.onebot,
        onebot_connected: payload.onebot.connected,
        onebot_reply_ready: payload.onebot.reply_ready,
        onebot_reply_blocker: payload.onebot.reply_blocker
      };
    }
    window.setTimeout(() => loadStatus(false, true), 1800);
  }
});
const autoRefresh = ref(true);
const lastLoadedAt = ref<Date | null>(null);
const autoQrAttempted = ref(false);
let timer: number | undefined;
const statusText = computed(() => status.value.is_login ? (status.value.is_online ? "在线" : "已登录/离线") : "未登录");
const qrcodeDataUrl = computed(() => qrcode.value.startsWith("data:") || qrcode.value.startsWith("http") ? qrcode.value : `data:image/png;base64,${qrcode.value}`);
const visibleQrcodeUrl = computed(() => qrcodeUrl.value || status.value.qrcode_url || "");
const qrcodeState = computed(() => qrcode.value ? "已获取" : (visibleQrcodeUrl.value ? "有链接" : "未获取"));
const formattedStatus = computed(() => JSON.stringify(status.value || {}, null, 2));
const onebotRuntime = computed<OneBotRuntimePayload>(() => status.value.onebot || {});
const onebotIngressVerdict = computed<OneBotIngressVerdict>(() => status.value.onebot_ingress_verdict || {});
const onebotReplyReadyLabel = computed(() => formatOnebotReplyReadyLabel(onebotRuntime.value));
const onebotReplyBlockerLabel = computed(() => onebotDiagnosisLabel(onebotRuntime.value.reply_blocker || status.value.onebot_reply_blocker));
const connectionLevel = computed(() => {
  return onebotIngressVerdict.value.level || (status.value.is_login || status.value.has_account ? "warning" : "error");
});
const connectionVerdict = computed(() => {
  if (onebotIngressVerdict.value.summary) return onebotIngressVerdict.value.summary;
  if (status.value.onebot_connected && status.value.is_online && onebotRuntime.value.reply_ready) return "QQ 在线，OneBot 可回复";
  if (status.value.onebot_connected && status.value.is_online) return "QQ 在线，OneBot 待验证";
  if (status.value.is_login && !status.value.is_online) return "QQ 已登录但离线";
  if (status.value.has_account) return "有账号缓存，可尝试快速登录";
  return "未发现可用登录态";
});
const connectionHint = computed(() => {
  if (status.value.login_error) return `NapCat: ${status.value.login_error}`;
  if (status.value.login_recovery_hint) return status.value.login_recovery_hint;
  if (onebotIngressVerdict.value.detail) return onebotIngressVerdict.value.detail;
  if (!status.value.onebot_connected) return "OneBot WebSocket 未连接，登录后仍需要确认反向连接。";
  if (!onebotRuntime.value.reply_ready) return `OneBot 已连接，但 QQ 消息入口尚未证明可回复：${onebotReplyBlockerLabel.value}`;
  if (!status.value.is_online) return "如果刚扫码或快速登录，请等待自动刷新确认在线状态。";
  return "连接状态正常，可以开始监听真实 QQ 消息。";
});
const diagnostics = computed(() => [
  { label: "NapCat API", value: status.value.code === 0 ? "可访问" : "异常", level: status.value.code === 0 ? "ok" : "error" },
  { label: "当前账号", value: status.value.uin || status.value.uid || "-", level: status.value.is_login ? "ok" : "warning" },
  { label: "QQ 在线", value: status.value.is_online ? "在线" : "离线", level: status.value.is_online ? "ok" : "warning" },
  { label: "OneBot", value: status.value.onebot_connected ? "已连接" : "未连接", level: status.value.onebot_connected ? "ok" : "error" },
  { label: "回复就绪", value: onebotReplyReadyLabel.value, level: onebotRuntime.value.reply_ready ? "ok" : "warning" },
  { label: "入口诊断", value: onebotIngressLabel(onebotIngressVerdict.value.code), level: onebotIngressVerdict.value.level || "warning" },
  { label: "入口阻断", value: onebotRuntime.value.reply_ready ? "-" : onebotReplyBlockerLabel.value, level: onebotRuntime.value.reply_ready ? "ok" : "warning" },
  { label: "登录建议", value: napcatLoginRecoveryActionLabel(status.value.login_recovery_action), level: status.value.is_online ? "ok" : "warning" },
  { label: "手动重连", value: String(onebotRuntime.value.manual_reconnect_count || 0), level: onebotRuntime.value.manual_reconnect_count ? "warning" : "ok" },
  { label: "登录错误", value: status.value.login_error || "-", level: status.value.login_error ? "error" : "ok" }
]);
const qqUin = (qq: NapCatQuickLoginAccount) => qq.uin || qq.uid || "";
const qqLabel = (qq: NapCatQuickLoginAccount) => `${qq.nickName || qq.nick || qq.uin || qq.uid}${qq.uin ? ` (${qq.uin})` : ""}`;
const qqKey = (qq: NapCatQuickLoginAccount) => qq.uin || qq.uid || JSON.stringify(qq);
const quickStatus = (qq: NapCatQuickLoginAccount) => {
  if (typeof qq !== "object") return "可登录";
  if (qq.isLogin || qq.is_online || qq.online) return "可能在线";
  return qq.uid || qq.uin ? "可快速登录" : "账号信息不完整";
};
function setTimer() {
  if (timer) window.clearInterval(timer);
  timer = autoRefresh.value ? window.setInterval(() => loadStatus(false, true), 5000) : undefined;
}

async function loadStatus(setBusy = false, silent = false) {
  if (setBusy) loading.value = true;
  if (!silent) error.value = "";
  try {
    status.value = await api<NapCatStatusPayload>("/api/napcat/status");
    lastLoadedAt.value = new Date();
    await maybeAutoFetchQrcode();
  } catch (err) {
    if (!silent) {
      error.value = err instanceof Error ? err.message : "NapCat 状态加载失败";
      toast.push(error.value, "error");
    }
  } finally {
    if (setBusy) loading.value = false;
  }
}

async function loadQuickList() {
  const payload = await api<{ code?: number; message?: string; list: NapCatQuickLoginAccount[] }>("/api/napcat/quick-login-list");
  if (payload.code !== 0) throw new Error(payload.message || "快速登录账号加载失败");
  quickList.value = payload.list || [];
}

async function loadAll() {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await Promise.all([loadStatus(false), loadQuickList()]);
    actionResult.value = "QQ 登录状态已刷新";
  } catch (err) {
    error.value = err instanceof Error ? err.message : "QQ 登录状态刷新失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function maybeAutoFetchQrcode() {
  if (status.value.login_recovery_action !== "scan_qrcode") {
    autoQrAttempted.value = false;
    return;
  }
  if (autoQrAttempted.value || qrcode.value || qrLoading.value) return;
  autoQrAttempted.value = true;
  await fetchQrcode({ silent: true });
}
async function fetchQrcode(options: { silent?: boolean; resultMessage?: string } = {}) {
  const silent = options.silent ?? false;
  qrLoading.value = true;
  if (!silent) {
    error.value = "";
    actionResult.value = "";
  }
  try {
    const payload = await postJson<any>("/api/napcat/qrcode", {});
    if (payload.code !== 0) throw new Error(payload.message || "获取二维码失败");
    qrcode.value = payload.qrcode || "";
    qrcodeUrl.value = payload.qrcode_url || "";
    if (!silent) actionResult.value = options.resultMessage || "二维码已获取，请尽快扫码";
    return true;
  } catch (err) {
    if (!silent) {
      error.value = err instanceof Error ? err.message : "获取二维码失败";
      toast.push(error.value, "error");
    }
    return false;
  } finally {
    qrLoading.value = false;
  }
}
async function getQrcode() {
  await fetchQrcode();
}
async function refreshQrcode() {
  qrLoading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const payload = await postJson<any>("/api/napcat/qrcode/refresh", {});
    if (payload.code !== 0) throw new Error(payload.message || "刷新二维码失败");
    await fetchQrcode({ resultMessage: "二维码已刷新，请尽快扫码" });
  } catch (err) {
    error.value = err instanceof Error ? err.message : "刷新二维码失败";
    toast.push(error.value, "error");
  } finally {
    qrLoading.value = false;
  }
}
async function quickLogin(uin: string) {
  if (!uin) {
    error.value = "账号缺少 uin，无法快速登录";
    return;
  }
  quickLoadingUin.value = uin;
  error.value = "";
  actionResult.value = "";
  try {
    const payload = await postJson<any>("/api/napcat/quick-login", { uin });
    if (payload.code !== 0) throw new Error(payload.message || "快速登录失败");
    actionResult.value = payload.message || "已发送快速登录";
    toast.push(actionResult.value);
    await loadStatus();
  } catch (err) {
    error.value = err instanceof Error ? err.message : "快速登录失败";
    toast.push(error.value, "error");
  } finally {
    quickLoadingUin.value = "";
  }
}
async function restart() {
  if (!window.confirm("确定重启 NapCat？")) return;
  restarting.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const payload = await postJson<any>("/api/napcat/restart", {});
    if (payload.code !== 0) throw new Error(payload.message || "重启失败");
    actionResult.value = payload.message || "已发送重启指令";
    toast.push(actionResult.value);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "重启 NapCat 失败";
    toast.push(error.value, "error");
  } finally {
    restarting.value = false;
  }
}
async function copyStatus() {
  const payload = {
    status: status.value,
    onebot_ingress_verdict: onebotIngressVerdict.value,
    quick_login_count: quickList.value.length,
    qrcode_url: visibleQrcodeUrl.value,
    refreshed_at: lastLoadedAt.value?.toISOString() || ""
  };
  await copyJson(payload, "QQ 登录诊断已复制");
}
function toggleAutoRefresh() {
  autoRefresh.value = !autoRefresh.value;
  setTimer();
  actionResult.value = autoRefresh.value ? "已开启自动刷新" : "已暂停自动刷新";
}
onMounted(() => { loadAll(); setTimer(); });
onBeforeUnmount(() => { if (timer) window.clearInterval(timer); });
</script>

<style scoped>
.save-result {
  border: 1px solid #386f47;
  background: #142016;
  color: var(--success);
  border-radius: 8px;
  padding: 10px 12px;
}

.login-verdict {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 12px;
  margin-bottom: 14px;
}

.login-verdict strong,
.login-verdict span {
  display: block;
}

.login-verdict span {
  color: var(--muted);
  margin-top: 5px;
}

.login-verdict.ok {
  border-color: #386f47;
}

.login-verdict.warning {
  border-color: #78612d;
}

.login-verdict.error {
  border-color: #743434;
}

.chips {
  align-items: stretch;
}

.account-chip {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  color: var(--text);
  padding: 10px 12px;
  min-width: 170px;
  text-align: left;
  cursor: pointer;
}

.account-chip strong,
.account-chip span {
  display: block;
}

.account-chip span {
  color: var(--muted);
  font-size: 12px;
  margin-top: 4px;
}

.diagnostic-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.diagnostic-grid div {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px;
}

.diagnostic-grid div.ok {
  border-color: #386f47;
}

.diagnostic-grid div.warning {
  border-color: #78612d;
}

.diagnostic-grid div.error {
  border-color: #743434;
}

.diagnostic-grid span {
  display: block;
  color: var(--muted);
  font-size: 12px;
}

.diagnostic-grid strong {
  display: block;
  margin-top: 6px;
  word-break: break-all;
}

.raw-status {
  margin-top: 12px;
}
</style>
